import React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import Icon from 'dpl/common/components/Icon';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { breederTransportOptionsQueryDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { FILTER_LOCATION_NAME } from 'dpl/constants/query_params';
import LoadingSpinner from 'dpl/components/LoadingSpinner';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';

import TransportDetailsDescription from './TransportDetailsDescription';
import { TRANSPORT_DETAILS_TYPES } from './utils/constants';

const { HOME, AIRPORT, MEETUP } = TRANSPORT_DETAILS_TYPES;

const IS_LT_MD = isLessThanBreakpoint('md');
const ICON_SIZE = IS_LT_MD ? '22px' : '24px';

function getTransportDetailsHeader(transportType: string) {
  if (transportType === HOME) {
    return 'Pick-up details';
  } else if (transportType === AIRPORT) {
    return 'Airport pick-up options';
  } else if (transportType === MEETUP) {
    return 'Meet-up options';
  }
  return null;
}

interface TransportDetailsModalContentProps {
  transportType: string;
}

function TransportDetailsModalContent({
  transportType
}: TransportDetailsModalContentProps) {
  const breederProfileId = useSelector(
    ({ server }: any) => server.breederProfileId
  );
  const breederFirstName = useSelector(
    ({ server }: any) => server.breederFirstName
  );
  const locName = useSelector(
    ({ queryParams }: any) => queryParams[FILTER_LOCATION_NAME]
  );

  const {
    isFetching: isFetchingTransportOptions,
    value: {
      data: {
        transport_details_options: transportDetailsOptions,
        drop_off_description: dropOffDescription,
        drop_off_pickup_from_your_location: hasDropOffPickupFromYourLocation,
        drop_off_pickup_from_your_location_description:
          dropOffPickupFromYourLocationDescription,
        airport_description: airportDescription,
        has_free_drop_off: hasFreeDropOff,
        drop_off_fee_type: dropOffFeeType,
        drop_off_fee_type_description: dropOffFeeTypeDescription
      }
    }
  } = useResourceQuery(
    breederTransportOptionsQueryDefinition(breederProfileId, { locName })
  );

  if (isFetchingTransportOptions) {
    return <LoadingSpinner />;
  }

  const options = transportDetailsOptions.filter(
    (option: any) => option.type === transportType
  );

  function getTransportDetails() {
    if (transportType === HOME) {
      return {
        customDescription: dropOffPickupFromYourLocationDescription || null,
        defaultDescription: hasDropOffPickupFromYourLocation
          ? `${breederFirstName || 'Your breeder'} allows pick-up at their home. ${breederFirstName || 'Your breeder'} will work with you to schedule timing as your puppy's go-home day approaches.`
          : `${breederFirstName || 'Your breeder'} will work with you to arrange the pickup location and timing as your puppy's go-home day approaches.`
      };
    } else if (transportType === AIRPORT) {
      return {
        customDescription: airportDescription || null,
        defaultDescription: `${breederFirstName || 'Your breeder'} offers flexible airport pick-up options. ${breederFirstName || 'Your breeder'} or a trusted transporter will meet you at the airport with your puppy.`
      };
    } else if (transportType === MEETUP) {
      return {
        customDescription: dropOffDescription || null,
        defaultDescription: `${breederFirstName || 'Your breeder'} will work with you to arrange a convenient meetup location for puppy pickup. Together, you'll decide on a safe, comfortable space that works well for both parties.`
      };
    }
    return null;
  }

  const transportDetails = getTransportDetails();
  const additionalSubtitle =
    transportType === HOME
      ? ` · ${breederFirstName || 'Your breeder'}'s location`
      : null;
  const modalHeader = getTransportDetailsHeader(transportType);

  return (
    <div
      className="TransportDetailsModalContent overflow-y-auto ph5 nh5 nh0-md ph8-md pb8-md"
      style={{ maxHeight: '80vh' }}
    >
      {IS_LT_MD && <h2 className="font-20 lh-title mb6">{modalHeader}</h2>}
      <div className="flex flex-column gap-20 md:gap-24">
        <TransportDetailsDescription
          customDescription={transportDetails!.customDescription}
          defaultDescription={transportDetails!.defaultDescription}
          transportType={transportType}
          hasFreeDropOff={hasFreeDropOff}
          dropOffFeeType={dropOffFeeType}
          dropOffFeeTypeDescription={dropOffFeeTypeDescription}
        />
        <ul>
          {options.length > 0 &&
            options.map(
              (
                { title, subtitle, icon, long_title: longTitle }: any,
                index: number
              ) => (
                <li
                  key={index}
                  className={classnames({
                    'bt b--default pt4 pt6-md': index > 0,
                    'pb4 pb6-md': index < options.length - 1
                  })}
                >
                  <div className="flex items-center gap-16">
                    <div className="ba b--default br3 secondary ph2 pv2 ph3-md pv3-md">
                      <Icon name={icon} width={ICON_SIZE} height={ICON_SIZE} />
                    </div>
                    <div className="flex flex-column font-14 md:font-18 lh-title gap-4">
                      <p className="fw-medium default-color">
                        {longTitle || title}
                      </p>
                      {subtitle && (
                        <p className="secondary">
                          {subtitle}
                          {additionalSubtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              )
            )}
        </ul>
      </div>
    </div>
  );
}

interface TransportDetailsModalProps {
  onCloseClick: () => void;
  transportType: string;
  isOpen: boolean;
}

export default function TransportDetailsModal({
  onCloseClick,
  transportType,
  isOpen
}: TransportDetailsModalProps) {
  if (!IS_LT_MD && !isOpen) {
    return null;
  }

  const modalHeader = getTransportDetailsHeader(transportType);

  if (IS_LT_MD) {
    return (
      <FixedTooltipPortal
        className="TransportDetailsModal"
        isVisible={isOpen}
        style={{ maxHeight: '85vh' }}
        onCloseClick={onCloseClick}
      >
        <div className="ph1 pt3">
          <TransportDetailsModalContent transportType={transportType} />
        </div>
      </FixedTooltipPortal>
    );
  }

  return (
    <MiniOverlayModal
      className="TransportDetailsModal"
      contentClassName="bg-white br3 min-content-h"
      scrollableContent
      closeButtonClassName="pv2 ph2 mt4 mr4 ba b--light-gray bg-white br-100"
      onCloseClick={onCloseClick}
    >
      <div className="flex flex-column h-100 overflow-hidden">
        <h2 className="font-20 md:font-24 lh-title mb6 mt8 ph5 ph8-md">
          {modalHeader}
        </h2>
        <div className="max-content-h">
          <TransportDetailsModalContent transportType={transportType} />
        </div>
      </div>
    </MiniOverlayModal>
  );
}
